import React from "react";

import i18n from "i18next";

import {
  Button,
  TableCellLayout,
  TableColumnDefinition,
  Text,
  Link,
  Tooltip,
  createTableColumn,
} from "@fluentui/react-components";
import { IStuelipos } from "../../Models/IStuelipos";
import {
  ArrowRepeatAllRegular,
  ArrowTurnDownRightRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  DocumentSearchRegular,
  ShoppingBagAddRegular,
} from "@fluentui/react-icons";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import { IDokument } from "../../Models/IDokument";
import { IAddUserBedarfPosition } from "../../Models/IAddUserBedarfPosition";

interface IBOMColumnsPros {
  showExpandButton: boolean;
  expandBom?(bomItem: IStuelipos): void;
  onAddBedarf(item: IAddUserBedarfPosition): void;
  showPdfViewerButton: boolean;
  showPdfViewer?(data: IDokument): void;
}

export function BOMColumns(props: IBOMColumnsPros) {
  const getStueliDoc = (item: IStuelipos) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/documents/GetDocumentForArtikel?artikelid=${item.artikelId}&docKey=ETZ`
      )
      .then((data: IDokument) => {
        if (props.showPdfViewer) {
          props.showPdfViewer(data);
        }
      })
      .catch((error) => {
        if (typeof error === "object" && error.hasOwnProperty("message")) {
          if (error.message == "NO_DOCUMENT_FOUND") {
          }
        }
      });
  };

  let columns: TableColumnDefinition<IStuelipos>[] = [
    createTableColumn<IStuelipos>({
      columnId: "position",
      renderHeaderCell: () => {
        return "Pos";
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              <Text weight={item.expanded ? "bold" : undefined}>
                {item.ebene != undefined && item.ebene > 0 && item.position
                  ? item.position
                  : ""}
                {item.positionNr ? item.positionNr : ""}
              </Text>
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IStuelipos>({
      columnId: "artikelId",
      renderHeaderCell: () => {
        return i18n.t<string>("artikelId");
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {(item.artikel == undefined || !item.hasSachwerte) && (
                <Text weight={item.expanded ? "bold" : undefined}>
                  {item.artikelId}
                </Text>
              )}
              {item.artikel != undefined && item.hasSachwerte && (
                <Link
                  style={{ fontWeight: item.expanded ? "bold" : undefined }}
                  target="_blank"
                  href={`${baseUrl}/quickfinder?guid=${item.artikel.guid}`}
                >
                  {item.artikelId}
                </Link>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IStuelipos>({
      columnId: "isVerschleissteil",
      renderHeaderCell: () => {
        return "";
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {item.isVerschleissteil && (
                <Tooltip
                  relationship="label"
                  content={i18n.t<string>("isVerschleissteilInfo")}
                >
                  <ArrowRepeatAllRegular color="#ff6600" />
                </Tooltip>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IStuelipos>({
      columnId: "menge",
      renderHeaderCell: () => {
        return i18n.t<string>("mengeCol");
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {item.position != 1 && (
                <Text weight={item.expanded ? "bold" : undefined}>
                  {(item.ebene != undefined && item.ebene > 0) ||
                  item.positionNr
                    ? item.menge
                    : ""}
                </Text>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IStuelipos>({
      columnId: "benennung",
      renderHeaderCell: () => {
        return i18n.t<string>("benennung");
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {!item.artikel?.benennungDE && (
                <Text weight={item.expanded ? "bold" : undefined}>
                  {i18n.language.includes("de")
                    ? item.benennungDE
                    : item.benennungEN}
                </Text>
              )}

              {item.artikel?.benennungDE && (
                <Text weight={item.expanded ? "bold" : undefined}>
                  {i18n.language.includes("de")
                    ? item.artikel.benennungDE
                    : item.artikel.benennungEN}
                </Text>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IStuelipos>({
      columnId: "actions",
      renderHeaderCell: () => {
        return "";
      },
      renderCell: (item: IStuelipos) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {item.hasDocuments && props.showPdfViewerButton && (
                <Tooltip
                  relationship="label"
                  content={i18n.t<string>("showETZ")}
                >
                  <Button
                    onClick={() => {
                      // Stüli Doc öffnen
                      getStueliDoc(item);
                    }}
                    appearance="subtle"
                    icon={<DocumentSearchRegular color="#ff6600" />}
                  />
                </Tooltip>
              )}
              {item.canAddToBedarf && (
                <Tooltip
                  relationship="label"
                  content={i18n.t<string>("addToBedarf")}
                >
                  <Button
                    onClick={() => {
                      props.onAddBedarf({
                        menge: item.menge ?? 0,
                        artikel: item.artikelId,
                        produkt: item.seriennummer,
                        benennung: i18n.language.includes("de")
                          ? item.benennungDE
                          : item.benennungEN,
                        reference: undefined,
                        kundenReferenz: undefined,
                      });
                    }}
                    appearance="subtle"
                    icon={<ShoppingBagAddRegular color="#ff6600" />}
                  />
                </Tooltip>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
  ];

  if (props.showExpandButton) {
    columns.splice(
      0,
      0,
      createTableColumn<IStuelipos>({
        columnId: "expand",
        renderHeaderCell: () => {
          return "";
        },
        renderCell: (item: IStuelipos) => {
          return (
            <TableCellLayout>
              <div className="lueb-centerVertical">
                {item.hasStueli && (
                  <div
                    style={{
                      marginLeft:
                        item.ebene != undefined ? item.ebene * 20 : undefined,
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (props.expandBom) {
                          props.expandBom(item);
                        }
                      }}
                      appearance="subtle"
                      icon={
                        item.expanded ? (
                          <ChevronDownRegular />
                        ) : (
                          <ChevronRightRegular />
                        )
                      }
                    />
                  </div>
                )}
                {!item.hasStueli &&
                  item.ebene != undefined &&
                  item.ebene > 0 && (
                    <div
                      style={{
                        marginLeft:
                          item.ebene != undefined
                            ? item.ebene * 20 - 10
                            : undefined,
                      }}
                    >
                      <ArrowTurnDownRightRegular />
                    </div>
                  )}
                {!item.hasStueli &&
                  item.ebene != undefined &&
                  item.ebene == 0 && <div></div>}
              </div>
            </TableCellLayout>
          );
        },
      })
    );
  }

  return columns;
}
